/* Locations — one record per site.
 *
 * This exists because the address did not. It was a constant in
 * services/depots.ts, invented when the shell was built and rendered in three
 * places as though somebody had checked it — a header tooltip, a dashboard card
 * and the network panel's subtitle all confidently showing a street nobody had
 * verified. That is the 60%-accurate register §17 warns about, in miniature:
 * wrong and indistinguishable from right.
 *
 * The register is also the list of sites now. It used to be a constant, which
 * made opening a fourth depot an edit to a union type, two mapping tables, a CSV
 * importer and a provisioning script — six places and a deploy, to record that
 * Hird had rented a building. Adding a site is adding a row.
 *
 * One thing cannot be helped from here: the Depot columns in SharePoint are
 * Choice columns, and a choice value has to exist before a person or a device
 * can be filed against it. The screen says so when a site has no records at
 * all, and provisioning/Add-LedgerDepot.ps1 is the one command that does it.
 *
 * Shaped on IT Glue's Locations, which is the part of it that works well: an
 * address, a way to ring the place, who to ask, and enough about the building
 * for somebody who has never been there to find the comms room.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDepotScope } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { useOnline } from '../../hooks/useOnline';
import { recordHref } from '../../hooks/useOpenRecord';
import { locationFields, valuesSeen } from '../../lib/registerFields';
import type { DepotId, ILocation } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Button,
  Controls,
  EmptyState,
  KeyValue,
  LoadError,
  Panel,
  RelationCard,
  Segmented,
  TableSkeleton,
} from '../ui';
import { Copyable } from './Copyable';
import { EditDrawer } from './EditDrawer';
import s from './screens.module.scss';

export function LocationsScreen() {
  const svc = useServices();
  const online = useOnline();
  const { scope, setScope } = useDepotScope();

  const depots = useDepots();
  const locations = useQuery({ queryKey: qk.locations, queryFn: () => svc.locations.all() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });

  /* Same reasoning as the network screen: "all depots" is not a useful view of a
     building, so this keeps its own selection seeded from the header's scope. */
  const [shown, setShown] = useState<DepotId | null>(null);
  const depot: DepotId =
    shown ?? (scope !== 'all' ? (scope as DepotId) : (depots[0]?.id as DepotId));

  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);

  const open = useMutation({
    mutationFn: (name: string) => svc.locations.create(name),
    onSuccess: (_created, name) => {
      void qc.invalidateQueries({ queryKey: qk.locations });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setAdding(false);
      setShown(name);
      setScope(name);
    },
  });

  const save = useMutation({

    meta: { ok: 'Location saved' },
    mutationFn: (patch: Partial<ILocation>) => svc.locations.save(depot, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.locations });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setEditing(false);
    },
  });

  const here = locations.data?.[depot];
  const depotMeta = useMemo(() => depots.find((d) => d.id === depot), [depots, depot]);
  const contact = (people.data ?? []).find((p) => p.id === here?.contact);

  const based = useMemo(
    () => (people.data ?? []).filter((p) => p.loc === depot && p.status !== 'Leaver'),
    [people.data, depot],
  );
  const kit = useMemo(
    () => (devices.data ?? []).filter((d) => d.loc === depot),
    [devices.data, depot],
  );

  /* The finding, and the reason this screen leads with it. An address nobody has
     filled in is honest; the invented one it replaced was not. */
  const unfilled = here !== undefined && !here.addr.trim();

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Locations</h1>
          <p>
            One record per site — where it is, who to ring, and how to get into the comms
            room.
          </p>
        </div>
        <Button
          icon="ti-plus"
          onClick={() => {
            const name = window.prompt('Name of the new site, as it should read everywhere:');
            if (!name?.trim()) return;
            setAdding(true);
            open.mutate(name.trim());
          }}
          disabled={!online || adding}
        >
          {adding ? 'Opening…' : 'Add site'}
        </Button>
        <Button primary icon="ti-pencil" onClick={() => setEditing(true)} disabled={!online}>
          Edit site
        </Button>
      </div>

      {locations.isError && <LoadError error={locations.error} what="the locations" />}

      <Controls>
        <Segmented
          label="Site"
          value={depot}
          onChange={(v) => {
            setShown(v as DepotId);
            setScope(v as DepotId);
          }}
          options={depots.map((d) => ({ value: d.id, label: d.name }))}
        />
      </Controls>

      {locations.isLoading ? (
        <Panel flush>
          <TableSkeleton rows={6} />
        </Panel>
      ) : !here ? (
        <Panel flush>
          <EmptyState icon="ti-map-pin" title="No record for this site" />
        </Panel>
      ) : (
        <div className={s.stack}>
          {unfilled && (
            <div className={s.ownedNote}>
              <i className="ti ti-alert-triangle" aria-hidden />
              <span>
                No address recorded for {depotMeta?.name}. Until this was a register the app
                showed one that had been made up when it was built — blank is the more useful
                answer, but only until somebody fills it in.
              </span>
            </div>
          )}

          <Panel title={depotMeta?.name} sub={here.role || depotMeta?.sub}>
            <KeyValue
              rows={[
                { section: 'Address', key: 'Address', value: here.addr },
                {
                  section: 'Address',
                  key: 'Postcode',
                  value: here.postcode ? <Copyable value={here.postcode} /> : '',
                },
                {
                  section: 'Address',
                  key: 'Main number',
                  value: here.phone ? <Copyable value={here.phone} /> : '',
                },

                {
                  section: 'Who to ask',
                  key: 'Site contact',
                  value: contact ? (
                    <a href={recordHref('/people', contact.upn)}>{contact.name}</a>
                  ) : (
                    ''
                  ),
                },
                { section: 'Who to ask', key: 'Opening hours', value: here.hours },

                { section: 'The building', key: 'Comms room', value: here.comms },
                { section: 'The building', key: 'Access', value: here.access },
                { section: 'The building', key: 'Notes', value: here.notes },
              ]}
            />
          </Panel>

          {based.length === 0 && kit.length === 0 && (
            <div className={s.ownedNote}>
              <i className="ti ti-info-circle" aria-hidden />
              <span>
                Nothing is filed at {depotMeta?.name} yet. If this is a site you have just
                added, the Depot columns in SharePoint are Choice columns and need
                "{depotMeta?.name}" as an option before anyone can be assigned to it — run{' '}
                <code>./Add-LedgerDepot.ps1 -Name "{depotMeta?.name}"</code> from the
                provisioning folder.
              </span>
            </div>
          )}

          <Panel title="What is here" flush>
            {/* Both of these are counts the registers already knew and nothing
                asked. "Who is at Doncaster" was a filter somebody had to
                remember to apply. */}
            <RelationCard
              icon="ti-users"
              title="People based here"
              count={based.length}
              preview={based.slice(0, 4).map((p) => p.name).join(' · ')}
            >
              {based.map((p) => (
                <a key={p.id} className={`${s.row} ${s.rowFlush}`} href={recordHref('/people', p.upn)}>
                  <div className={s.rowIcon}>
                    <i className="ti ti-user" aria-hidden />
                  </div>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{p.name}</div>
                    <div className={s.rowSub}>{p.title || 'No job title'}</div>
                  </div>
                </a>
              ))}
            </RelationCard>

            <RelationCard
              icon="ti-devices"
              title="Devices here"
              count={kit.length}
              preview={
                kit.length > 0
                  ? `${kit.filter((d) => d.type === 'Laptop' || d.type === 'Desktop').length} endpoints · ${
                      kit.filter((d) => !['Laptop', 'Desktop', 'Mobile', 'Tablet'].includes(d.type)).length
                    } infrastructure`
                  : undefined
              }
            >
              {kit.slice(0, 40).map((d) => (
                <a key={d.id} className={`${s.row} ${s.rowFlush}`} href={recordHref('/devices', d.tag)}>
                  <div className={s.rowIcon}>
                    <i className="ti ti-device-laptop" aria-hidden />
                  </div>
                  <div className={s.rowMain}>
                    <div className={s.rowTitle}>{d.tag}</div>
                    <div className={s.rowSub}>
                      {d.make} {d.model} · {d.type}
                    </div>
                  </div>
                </a>
              ))}
            </RelationCard>

            <RelationCard
              icon="ti-network"
              title="Circuit and addressing"
              count={1}
              preview="Subnets, wireless and tunnels for this site"
            >
              <a className={`${s.row} ${s.rowFlush}`} href="/network">
                <div className={s.rowIcon}>
                  <i className="ti ti-network" aria-hidden />
                </div>
                <div className={s.rowMain}>
                  <div className={s.rowTitle}>Network — {depotMeta?.name}</div>
                  <div className={s.rowSub}>The circuit to quote and the gateway to check</div>
                </div>
              </a>
            </RelationCard>
          </Panel>
        </div>
      )}

      <EditDrawer
        open={editing}
        record={here ?? null}
        fields={locationFields(valuesSeen(Object.values(locations.data ?? {}), ['role', 'hours']))}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="site"
        icon="ti-map-pin"
        title={`${depotMeta?.name ?? depot} — site details`}
        onClose={() => setEditing(false)}
        onSave={(values) => save.mutate(values as Partial<ILocation>)}
        saving={save.isPending}
        error={save.error}
      >
        <div className={s.ownedNote}>
          <i className="ti ti-info-circle" aria-hidden />
          <span>
            The site's name is its identity everywhere — it is what every Depot column
            stores — so it is set when the site is opened and not editable here. Everything
            else on this form is yours.
          </span>
        </div>
      </EditDrawer>
    </>
  );
}
